import React, { useState, useEffect, useCallback } from 'react';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  GraduationCap, Loader2, FlaskConical, GitBranch, BookOpen, Brain, Plus,
  CheckCircle2, Play, ShieldQuestion, TrendingUp,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EPISTEMIC_STYLE = {
  fact: 'bg-emerald-500/10 text-emerald-400', observation: 'bg-sky-500/10 text-sky-400',
  hypothesis: 'bg-amber-500/10 text-amber-400', recommendation: 'bg-purple-500/10 text-purple-400',
  decision: 'bg-orange-500/10 text-orange-400', result: 'bg-teal-500/10 text-teal-400',
};

const REC_STYLE = { scale: 'text-emerald-400', continue: 'text-sky-400', repeat: 'text-teal-400', modify: 'text-amber-400', stop: 'text-red-400' };

export default function LearningEnginePage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [insights, setInsights] = useState(null);
  const [records, setRecords] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [experiments, setExperiments] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [tab, setTab] = useState('records');
  const [storeFilter, setStoreFilter] = useState('');
  const [busy, setBusy] = useState(null);
  const [expOpen, setExpOpen] = useState(false);
  const [expForm, setExpForm] = useState({ hypothesis: '', objective: '', baseline: '', variable: '', control: '', expected_result: '', success_criteria: '', duration_days: 7 });
  const [resultDrafts, setResultDrafts] = useState({});

  const refresh = useCallback(async () => {
    try {
      const [i, r, k, e, s] = await Promise.all([
        axios.get(`${API}/autonomy/learning/insights`, { headers }),
        axios.get(`${API}/autonomy/learning/records?limit=40`, { headers }),
        axios.get(`${API}/autonomy/learning/knowledge?limit=60${storeFilter ? `&store=${storeFilter}` : ''}`, { headers }),
        axios.get(`${API}/autonomy/learning/experiments`, { headers }),
        axios.get(`${API}/autonomy/learning/strategies`, { headers }),
      ]);
      setInsights(i.data); setRecords(r.data); setKnowledge(k.data); setExperiments(e.data); setStrategies(s.data);
    } catch { /* transient */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, storeFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { await fn(); ok && toast.success(ok); await refresh(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed'); }
    finally { setBusy(null); }
  };

  const createExperiment = () => act('newexp', async () => {
    if (!expForm.hypothesis.trim()) throw { response: { data: { detail: 'Hypothesis is required' } } };
    await axios.post(`${API}/autonomy/learning/experiments`, expForm, { headers });
    setExpOpen(false);
    setExpForm({ hypothesis: '', objective: '', baseline: '', variable: '', control: '', expected_result: '', success_criteria: '', duration_days: 7 });
  }, 'Experiment created');

  if (!insights) {
    return <AppShell><div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center" data-testid="learning-loading"><Loader2 className="w-8 h-8 text-orange-500 animate-spin" /></div></AppShell>;
  }

  return (
    <AppShell>
      <div className="min-h-screen bg-[#0a0a0f] text-white pb-16" data-testid="learning-page">
        <div className="border-b border-white/[0.06] bg-[#0d0d14]/80 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
            <GraduationCap className="w-6 h-6 text-orange-400" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="learning-title">Learning Engine</h1>
              <p className="text-sm text-white/40">Evidence-based learning — lessons only from recorded outcomes, never assumptions</p>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
          {/* Insights */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="learning-insights">
            {[['Learning Records', insights.learning_records], ['Experiments', insights.experiments.total],
              ['Concluded', insights.experiments.concluded], ['Strategy Versions', insights.strategy_versions],
              ['Verified Facts', insights.knowledge_by_epistemic.fact], ['Blocked Fact Claims', insights.downgraded_fact_claims]].map(([l, v]) => (
              <div key={l} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3">
                <p className="text-lg font-bold text-white">{v}</p>
                <p className="text-[10px] text-zinc-500 uppercase tracking-wide">{l}</p>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2">
            {[['records', BookOpen, 'Learning Records'], ['knowledge', Brain, 'Memory Stores'], ['experiments', FlaskConical, 'Experiments'], ['strategies', GitBranch, 'Strategy Versions']].map(([id, Icon, label]) => (
              <button key={id} onClick={() => setTab(id)} data-testid={`ltab-${id}`}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${tab === id ? 'bg-orange-500/15 text-orange-400' : 'text-zinc-500 hover:text-white'}`}>
                <Icon className="w-3.5 h-3.5" />{label}
              </button>
            ))}
          </div>

          {tab === 'records' && (
            <div className="space-y-2" data-testid="records-list">
              {records.length === 0 && <p className="text-sm text-zinc-600 py-6 text-center">No learning records yet — they're created automatically when tasks and experiments produce recorded outcomes.</p>}
              {records.map(r => (
                <details key={r.id} className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-3" data-testid={`record-${r.id}`}>
                  <summary className="cursor-pointer flex flex-wrap items-center gap-2 text-xs">
                    <span className="px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 text-[9px] uppercase">{r.source}</span>
                    <span className="text-white flex-1 min-w-0 truncate font-medium">{r.lesson || r.delta || r.input}</span>
                    <span className="text-zinc-600">{new Date(r.created_at).toLocaleDateString()}</span>
                  </summary>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                    {[['Input', r.input], ['Decision', r.decision], ['Action', r.action], ['Expected', r.expected_outcome],
                      ['Actual', r.actual_outcome], ['Delta', r.delta], ['Lesson', r.lesson], ['Recommendation', r.recommendation]].map(([l, v]) => v && (
                      <div key={l}><span className="text-zinc-600 uppercase text-[9px]">{l}: </span><span className="text-zinc-300">{v}</span></div>
                    ))}
                    <div className="sm:col-span-2 text-[9px] text-zinc-600">Evidence: {(r.evidence_refs || []).join(', ') || '—'}</div>
                  </div>
                </details>
              ))}
            </div>
          )}

          {tab === 'knowledge' && (
            <div data-testid="knowledge-section">
              <div className="flex flex-wrap gap-1.5 mb-3">
                <button onClick={() => setStoreFilter('')} className={`px-2 py-1 rounded text-[10px] ${!storeFilter ? 'bg-orange-500/15 text-orange-400' : 'bg-zinc-800 text-zinc-500'}`}>all</button>
                {Object.entries(insights.knowledge_by_store).map(([s, n]) => (
                  <button key={s} onClick={() => setStoreFilter(s)} data-testid={`store-${s}`}
                    className={`px-2 py-1 rounded text-[10px] ${storeFilter === s ? 'bg-orange-500/15 text-orange-400' : 'bg-zinc-800 text-zinc-500'}`}>
                    {s.replace(/_/g, ' ')} ({n})
                  </button>
                ))}
              </div>
              <div className="space-y-1.5" data-testid="knowledge-list">
                {knowledge.length === 0 && <p className="text-sm text-zinc-600 py-6 text-center">No entries in this store yet.</p>}
                {knowledge.map(k => (
                  <div key={k.id} className="flex items-start gap-2 p-2.5 bg-zinc-900/60 border border-zinc-800 rounded-lg text-xs">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shrink-0 ${EPISTEMIC_STYLE[k.epistemic]}`}>{k.epistemic}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-zinc-200">{k.content}</p>
                      <p className="text-[9px] text-zinc-600 mt-0.5">
                        {k.store.replace(/_/g, ' ')} · evidence: {k.supporting_count}
                        {k.downgraded_from && <span className="text-amber-400"> · downgraded from {k.downgraded_from} (insufficient evidence)</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'experiments' && (
            <div data-testid="experiments-section">
              <Button size="sm" onClick={() => setExpOpen(!expOpen)} className="bg-orange-500 hover:bg-orange-600 mb-3 h-8" data-testid="new-experiment-btn">
                <Plus className="w-3 h-3 mr-1" />New Experiment
              </Button>
              {expOpen && (
                <div className="mb-4 p-4 bg-zinc-900/60 border border-zinc-800 rounded-xl grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="experiment-form">
                  {[['hypothesis', 'Hypothesis *'], ['objective', 'Objective'], ['baseline', 'Baseline'], ['variable', 'Variable'],
                    ['control', 'Control (where possible)'], ['expected_result', 'Expected result'], ['success_criteria', 'Success criteria']].map(([f, ph]) => (
                    <Input key={f} value={expForm[f]} onChange={e => setExpForm(v => ({ ...v, [f]: e.target.value }))}
                      placeholder={ph} className="bg-zinc-800/60 border-zinc-700" data-testid={`exp-${f}`} />
                  ))}
                  <Input type="number" value={expForm.duration_days} onChange={e => setExpForm(v => ({ ...v, duration_days: parseInt(e.target.value) || 7 }))}
                    placeholder="Duration (days)" className="bg-zinc-800/60 border-zinc-700" data-testid="exp-duration" />
                  <Button size="sm" onClick={createExperiment} disabled={busy === 'newexp'} className="bg-orange-500 hover:bg-orange-600 sm:col-span-2" data-testid="create-experiment-btn">
                    {busy === 'newexp' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Create Experiment'}
                  </Button>
                </div>
              )}
              <div className="space-y-2" data-testid="experiments-list">
                {experiments.length === 0 && <p className="text-sm text-zinc-600 py-6 text-center">No experiments yet.</p>}
                {experiments.map(x => (
                  <div key={x.id} className="p-3 bg-zinc-900/60 border border-zinc-800 rounded-xl" data-testid={`experiment-${x.id}`}>
                    <div className="flex flex-wrap items-center gap-2 text-xs">
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${x.status === 'concluded' ? 'bg-emerald-500/10 text-emerald-400' : x.status === 'running' ? 'bg-orange-500/10 text-orange-400' : 'bg-zinc-800 text-zinc-400'}`}>{x.status}</span>
                      <p className="text-white font-medium flex-1 min-w-0">{x.hypothesis}</p>
                      {x.status === 'draft' && (
                        <Button size="sm" onClick={() => act(x.id, () => axios.post(`${API}/autonomy/learning/experiments/${x.id}/start`, {}, { headers }), 'Experiment started')}
                          className="bg-white/[0.08] h-7 text-xs" data-testid={`start-exp-${x.id}`}>
                          <Play className="w-3 h-3 mr-1" />Start
                        </Button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 mt-1.5 text-[10px] text-zinc-500">
                      {x.baseline && <span>Baseline: {x.baseline}</span>}
                      {x.variable && <span>Variable: {x.variable}</span>}
                      {x.success_criteria && <span>Success: {x.success_criteria}</span>}
                    </div>
                    {x.status !== 'concluded' && (
                      <div className="flex gap-2 mt-2">
                        <Textarea rows={1} value={resultDrafts[x.id] ?? x.result} onChange={e => setResultDrafts(v => ({ ...v, [x.id]: e.target.value }))}
                          placeholder="Record observed result…" className="bg-zinc-800/60 border-zinc-700 text-xs flex-1" data-testid={`result-input-${x.id}`} />
                        <Button size="sm" onClick={() => act(`r-${x.id}`, () => axios.post(`${API}/autonomy/learning/experiments/${x.id}/record-result`, { result: resultDrafts[x.id] || '' }, { headers }), 'Result recorded')}
                          className="bg-white/[0.08] h-auto text-xs" data-testid={`record-result-${x.id}`}>Record</Button>
                        <Button size="sm" onClick={() => act(`c-${x.id}`, () => axios.post(`${API}/autonomy/learning/experiments/${x.id}/conclude`, {}, { headers }), 'Experiment concluded')}
                          disabled={busy === `c-${x.id}`} className="bg-emerald-600 hover:bg-emerald-700 h-auto text-xs" data-testid={`conclude-${x.id}`}>
                          {busy === `c-${x.id}` ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Conclude'}
                        </Button>
                      </div>
                    )}
                    {x.status === 'concluded' && (
                      <div className="mt-2 text-[11px]">
                        <p className="text-zinc-300"><CheckCircle2 className="w-3 h-3 text-emerald-400 inline mr-1" />{x.conclusion}</p>
                        <p className="mt-0.5"><span className={`font-bold uppercase ${REC_STYLE[x.next_action] || 'text-zinc-400'}`}>{x.next_action}</span>
                          <span className="text-zinc-600"> · confidence {Math.round(x.confidence * 100)}%</span></p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab === 'strategies' && (
            <div className="space-y-3" data-testid="strategies-section">
              {strategies.length === 0 && <p className="text-sm text-zinc-600 py-6 text-center">No strategy versions yet.</p>}
              {strategies.map(s => (
                <div key={s.id} className={`p-4 rounded-xl border ${s.status === 'active' ? 'border-orange-500/30 bg-orange-500/[0.04]' : 'border-zinc-800 bg-zinc-900/50'}`}
                  data-testid={`strategy-${s.id}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-orange-400">v{s.version}</span>
                    <p className="text-sm font-semibold text-white flex-1">{s.title}</p>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase font-bold ${s.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-zinc-800 text-zinc-500'}`}>{s.status}</span>
                  </div>
                  {s.content && <p className="text-xs text-zinc-400 mt-1">{s.content}</p>}
                  {s.rationale && <p className="text-[11px] text-zinc-500 mt-1.5"><ShieldQuestion className="w-3 h-3 inline mr-1 text-amber-400" />Rationale: {s.rationale}</p>}
                  {s.results_summary && <p className="text-[11px] text-zinc-400 mt-1"><TrendingUp className="w-3 h-3 inline mr-1 text-emerald-400" />Recorded results: {s.results_summary}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}
